// src/pages/KarigarManagement/Job/JobListPage.jsx
// UPDATED - Aligned with new backend API

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useJobManagement } from "@/hooks/useKarigar";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Badge } from "@/components/ui-v2/badge";
import { Card, CardContent } from "@/components/ui-v2/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui-v2/dropdown-menu";
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  UserPlus,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  AlertTriangle,
  Truck,
  ClipboardCheck,
} from "lucide-react";
import Pagination from "@/components/common/Pagination";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { debounce } from "lodash";
import { format } from "date-fns";

// ✅ FIXED: Status badge colors with UPPERCASE keys
const STATUS_COLORS = {
  PENDING: "bg-yellow-100 text-yellow-800",
  ASSIGNED: "bg-blue-100 text-blue-800",
  ACCEPTED: "bg-indigo-100 text-indigo-800",
  MATERIAL_ISSUED: "bg-cyan-100 text-cyan-800",
  MATERIAL_RECEIVED: "bg-teal-100 text-teal-800",
  IN_PROGRESS: "bg-purple-100 text-purple-800",
  SUBMITTED_FOR_QC: "bg-orange-100 text-orange-800",
  QUALITY_CHECK: "bg-orange-200 text-orange-900",
  REVISION_REQUIRED: "bg-amber-100 text-amber-800",
  APPROVED: "bg-green-200 text-green-900",
  DELIVERED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-red-100 text-red-800",
};

const PRIORITY_COLORS = {
  LOW: "bg-gray-100 text-gray-800",
  NORMAL: "bg-blue-100 text-blue-800",
  HIGH: "bg-orange-100 text-orange-800",
  URGENT: "bg-red-100 text-red-800",
  CRITICAL: "bg-red-200 text-red-900",
};

// ✅ NEW: Job Types for filter
const JOB_TYPES = [
  { value: "CUSTOM_ORDER", label: "Custom Order" },
  { value: "REPAIR", label: "Repair" },
  { value: "ALTERATION", label: "Alteration" },
  { value: "POLISH_RHODIUM", label: "Polish/Rhodium" },
  { value: "STONE_SETTING", label: "Stone Setting" },
  { value: "STOCK_ITEM", label: "Stock Item" },
  { value: "INTERNAL", label: "Internal Work" },
];

const JobListPage = () => {
  const navigate = useNavigate();
  const {
    jobs,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    getJobs,
    updateFilters,
    clearFilters,
    clearError,
    clearSuccessMessage,
  } = useJobManagement();

  const { branches } = useSelector((state) => state.branch || { branches: [] });
  const [searchInput, setSearchInput] = useState(filters.search || "");

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      updateFilters({ search: value });
    }, 500),
    []
  );

  // Fetch jobs on mount and filter change
  useEffect(() => {
    getJobs({
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    });
  }, [filters, pagination.page]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Handle filter changes
  const handleStatusFilter = (value) => {
    updateFilters({ status: value === "all" ? "" : value });
  };

  const handlePriorityFilter = (value) => {
    updateFilters({ priority: value === "all" ? "" : value });
  };

  const handleBranchFilter = (value) => {
    updateFilters({ branchCode: value === "all" ? "" : value });
  };

  // ✅ NEW: Handle job type filter
  const handleJobTypeFilter = (value) => {
    updateFilters({ jobType: value === "all" ? "" : value });
  };

  // Handle page change
  const handlePageChange = (page) => {
    getJobs({
      page,
      limit: pagination.limit,
      ...filters,
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    clearFilters();
  };

  // Navigate to job detail
  const handleViewJob = (jobId) => {
    navigate(`/karigar-management/jobs/${jobId}`);
  };

  // Navigate to assignment
  const handleAssignJob = (jobId) => {
    navigate(`/karigar-management/jobs/${jobId}/assign`);
  };

  // ✅ Helper to get karigar data from job (handles populated karigarId)
  const getKarigar = (job) => {
    // karigarId is populated as an object
    if (job.karigarId && typeof job.karigarId === "object") {
      return {
        ...job.karigarId,
        karigarId: job.karigarId._id,
      };
    }
    // Fallback to flat fields
    if (job.karigarName) {
      return {
        _id: job.karigarId,
        name: job.karigarName,
        karigarCode: job.karigarCode,
        phoneNumber: job.karigarPhone,
      };
    }
    return null;
  };

  // ✅ FIXED: Check if job is overdue - using UPPERCASE status values
  const isOverdue = (job) => {
    // Use backend computed field if available
    if (job.isOverdue) return true;
    if (!job.expectedCompletionDate) return false;
    // ✅ FIXED: Using UPPERCASE status values
    if (["APPROVED", "DELIVERED", "CANCELLED"].includes(job.status))
      return false;
    return new Date(job.expectedCompletionDate) < new Date();
  };

  // ✅ FIXED: Calculate stats with UPPERCASE status values
  const getStats = () => {
    return {
      pending: jobs.filter((j) => j.status === "PENDING").length,
      inProgress: jobs.filter((j) => j.status === "IN_PROGRESS").length,
      qualityCheck: jobs.filter(
        (j) => j.status === "SUBMITTED_FOR_QC" || j.status === "QUALITY_CHECK"
      ).length,
      overdue: jobs.filter(isOverdue).length,
    };
  };

  const stats = getStats();

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Job Management</h1>
          <p className="text-muted-foreground">Track and manage karigar jobs</p>
        </div>
        <Button onClick={() => navigate("/karigar-management/jobs/create")}>
          <Plus className="h-4 w-4 mr-2" />
          Create Job
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Quality Check</p>
                <p className="text-2xl font-bold">{stats.qualityCheck}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold">{stats.overdue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by job number, karigar..."
                value={searchInput}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>

            {/* ✅ FIXED: Status Filter with UPPERCASE values */}
            <Select
              value={filters.status || "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="ASSIGNED">Assigned</SelectItem>
                <SelectItem value="ACCEPTED">Accepted</SelectItem>
                <SelectItem value="MATERIAL_ISSUED">Material Issued</SelectItem>
                <SelectItem value="MATERIAL_RECEIVED">
                  Material Received
                </SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="SUBMITTED_FOR_QC">
                  Submitted for QC
                </SelectItem>
                <SelectItem value="QUALITY_CHECK">Quality Check</SelectItem>
                <SelectItem value="REVISION_REQUIRED">
                  Revision Required
                </SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* ✅ NEW: Job Type Filter */}
            <Select
              value={filters.jobType || "all"}
              onValueChange={handleJobTypeFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {JOB_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Priority Filter */}
            <Select
              value={filters.priority || "all"}
              onValueChange={handlePriorityFilter}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="NORMAL">Normal</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>

            {/* Branch Filter */}
            <Select
              value={filters.branchCode || "all"}
              onValueChange={handleBranchFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches?.map((branch) => (
                  <SelectItem
                    key={branch.code || branch._id}
                    value={branch.code}
                  >
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reset Filters */}
            <Button variant="outline" onClick={handleResetFilters}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading.list ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>No jobs found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Karigar</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const karigar = getKarigar(job);
                  const overdue = isOverdue(job);

                  return (
                    <TableRow
                      key={job._id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        overdue ? "bg-red-50" : ""
                      }`}
                      onClick={() => handleViewJob(job._id)}
                    >
                      {/* Job Info */}
                      <TableCell>
                        <div>
                          <p className="font-medium">{job.jobNumber}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {job.title}
                          </p>
                        </div>
                      </TableCell>

                      {/* Karigar */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {karigar ? (
                          <div
                            className="cursor-pointer hover:text-primary"
                            onClick={() =>
                              navigate(
                                `/karigar-management/karigars/${karigar._id}`
                              )
                            }
                          >
                            <p className="font-medium">{karigar.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {karigar.karigarCode}
                            </p>
                          </div>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-yellow-600 border-yellow-300"
                          >
                            Unassigned
                          </Badge>
                        )}
                      </TableCell>

                      {/* Job Type */}
                      <TableCell>
                        <p className="text-sm">
                          {job.jobType?.replace(/_/g, " ")}
                        </p>
                      </TableCell>

                      {/* Priority */}
                      <TableCell>
                        <Badge className={PRIORITY_COLORS[job.priority] || ""}>
                          {job.priority}
                        </Badge>
                        {job.isUrgent && (
                          <Badge className="ml-1 bg-red-500 text-white text-xs">
                            !
                          </Badge>
                        )}
                      </TableCell>

                      {/* Due Date - using expectedCompletionDate */}
                      <TableCell>
                        <div className="text-sm">
                          {job.expectedCompletionDate ? (
                            <>
                              <p>
                                {format(
                                  new Date(job.expectedCompletionDate),
                                  "dd MMM yyyy"
                                )}
                              </p>
                              {overdue && (
                                <p className="text-red-500 text-xs font-medium">
                                  Overdue!
                                </p>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Badge className={STATUS_COLORS[job.status] || ""}>
                          {job.status?.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>

                      {/* Actions */}
                      <TableCell
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleViewJob(job._id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>

                            {/* ✅ FIXED: Using UPPERCASE status values */}
                            {job.status === "PENDING" && (
                              <DropdownMenuItem
                                onClick={() => handleAssignJob(job._id)}
                              >
                                <UserPlus className="h-4 w-4 mr-2" />
                                Assign Karigar
                              </DropdownMenuItem>
                            )}

                            {job.status === "ASSIGNED" && karigar && (
                              <DropdownMenuItem
                                onClick={() => handleAssignJob(job._id)}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Reassign
                              </DropdownMenuItem>
                            )}

                            {/* ✅ FIXED: Quality Check action for correct statuses */}
                            {(job.status === "SUBMITTED_FOR_QC" ||
                              job.status === "QUALITY_CHECK") && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-green-600"
                                  onClick={() =>
                                    navigate(
                                      `/karigar-management/jobs/${job._id}?action=qc`
                                    )
                                  }
                                >
                                  <ClipboardCheck className="h-4 w-4 mr-2" />
                                  Quality Check
                                </DropdownMenuItem>
                              </>
                            )}

                            {/* ✅ FIXED: Mark Delivered for APPROVED status */}
                            {job.status === "APPROVED" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-emerald-600"
                                  onClick={() =>
                                    navigate(
                                      `/karigar-management/jobs/${job._id}?action=deliver`
                                    )
                                  }
                                >
                                  <Truck className="h-4 w-4 mr-2" />
                                  Mark Delivered
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="border-t p-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default JobListPage;
